import { MONSTERS } from '../config/tileIndex';
import { gameBridge } from './GameBridge';

/**
 * Unlockable character data
 */
export interface UnlockableCharacter {
  id: string;
  name: string;
  description: string;
  monsterFrame: number;         // Base frame in monsters spritesheet
  portraitClass?: string;       // For portrait display
  xpRequired: number;           // XP needed to unlock
  unlocked: boolean;
  unlockedAt?: string;          // ISO date when unlocked
}

/**
 * Achievement data
 */
export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;                 // Item frame or icon name
  requirement: AchievementRequirement;
  unlocked: boolean;
  unlockedAt?: string;
  progress?: number;            // Current progress (for trackable achievements)
}

interface AchievementRequirement {
  type: 'xp' | 'correct_answers' | 'streak' | 'subjects_mastered' | 'cards_seen' | 'perfect_runs';
  value: number;
}

/**
 * Progression data stored in localStorage
 */
interface ProgressionData {
  version: number;
  characters: UnlockableCharacter[];
  achievements: Achievement[];
  selectedCharacterId: string;
  totalXP: number;
  highestStreak: number;
  perfectRuns: number;
  subjectsMastered: string[];
}

const STORAGE_KEY = 'badgey-quest-progression';
const PROGRESSION_VERSION = 1;

/**
 * Default unlockable characters
 */
const DEFAULT_CHARACTERS: UnlockableCharacter[] = [
  {
    id: 'knight',
    name: 'Knight',
    description: 'A brave warrior ready for battle.',
    monsterFrame: MONSTERS.KNIGHT,
    portraitClass: 'fighter',
    xpRequired: 0,
    unlocked: true,
  },
  {
    id: 'rogue',
    name: 'Rogue',
    description: 'Quick and cunning, masters the shadows.',
    monsterFrame: MONSTERS.ROGUE,
    portraitClass: 'rogue',
    xpRequired: 500,
    unlocked: false,
  },
  {
    id: 'wizard',
    name: 'Wizard',
    description: 'Wielder of arcane knowledge.',
    monsterFrame: MONSTERS.WIZARD,
    portraitClass: 'wizard',
    xpRequired: 1000,
    unlocked: false,
  },
  {
    id: 'archer',
    name: 'Archer',
    description: 'Precise and deadly at range.',
    monsterFrame: MONSTERS.ARCHER,
    portraitClass: 'archer',
    xpRequired: 1500,
    unlocked: false,
  },
  {
    id: 'paladin',
    name: 'Paladin',
    description: 'Holy warrior of justice.',
    monsterFrame: MONSTERS.PALADIN,
    portraitClass: 'paladin',
    xpRequired: 2500,
    unlocked: false,
  },
  {
    id: 'cleric',
    name: 'Cleric',
    description: 'Healer and protector of the faithful.',
    monsterFrame: MONSTERS.CLERIC,
    portraitClass: 'cleric',
    xpRequired: 3500,
    unlocked: false,
  },
  {
    id: 'necromancer',
    name: 'Necromancer',
    description: 'Master of dark arts and the undead.',
    monsterFrame: MONSTERS.NECROMANCER,
    portraitClass: 'necromancer',
    xpRequired: 5000,
    unlocked: false,
  },
  {
    id: 'druid',
    name: 'Druid',
    description: 'Guardian of nature\'s balance.',
    monsterFrame: MONSTERS.DRUID,
    portraitClass: 'druid',
    xpRequired: 7500,
    unlocked: false,
  },
  {
    id: 'berserker',
    name: 'Berserker',
    description: 'Unstoppable fury in battle.',
    monsterFrame: MONSTERS.BERSERKER,
    portraitClass: 'berserker',
    xpRequired: 10000,
    unlocked: false,
  },
];

/**
 * Default achievements
 */
const DEFAULT_ACHIEVEMENTS: Achievement[] = [
  {
    id: 'first_steps',
    name: 'First Steps',
    description: 'Answer your first question correctly.',
    icon: 'star',
    requirement: { type: 'correct_answers', value: 1 },
    unlocked: false,
  },
  {
    id: 'scholar',
    name: 'Scholar',
    description: 'Answer 50 questions correctly.',
    icon: 'book',
    requirement: { type: 'correct_answers', value: 50 },
    unlocked: false,
    progress: 0,
  },
  {
    id: 'master_scholar',
    name: 'Master Scholar',
    description: 'Answer 500 questions correctly.',
    icon: 'tome',
    requirement: { type: 'correct_answers', value: 500 },
    unlocked: false,
    progress: 0,
  },
  {
    id: 'streak_5',
    name: 'On Fire',
    description: 'Get a 5-answer streak.',
    icon: 'flame',
    requirement: { type: 'streak', value: 5 },
    unlocked: false,
  },
  {
    id: 'streak_10',
    name: 'Unstoppable',
    description: 'Get a 10-answer streak.',
    icon: 'lightning',
    requirement: { type: 'streak', value: 10 },
    unlocked: false,
  },
  {
    id: 'streak_25',
    name: 'Legal Legend',
    description: 'Get a 25-answer streak.',
    icon: 'crown',
    requirement: { type: 'streak', value: 25 },
    unlocked: false,
  },
  {
    id: 'xp_1000',
    name: 'Rising Star',
    description: 'Earn 1,000 XP.',
    icon: 'gem',
    requirement: { type: 'xp', value: 1000 },
    unlocked: false,
    progress: 0,
  },
  {
    id: 'xp_10000',
    name: 'Bar Champion',
    description: 'Earn 10,000 XP.',
    icon: 'trophy',
    requirement: { type: 'xp', value: 10000 },
    unlocked: false,
    progress: 0,
  },
  {
    id: 'perfect_run',
    name: 'Perfect Run',
    description: 'Complete a subject run with no mistakes.',
    icon: 'star_gold',
    requirement: { type: 'perfect_runs', value: 1 },
    unlocked: false,
  },
  {
    id: 'perfect_five',
    name: 'Five Star General',
    description: 'Complete 5 perfect runs.',
    icon: 'medal',
    requirement: { type: 'perfect_runs', value: 5 },
    unlocked: false,
    progress: 0,
  },
  {
    id: 'explorer',
    name: 'Explorer',
    description: 'Study all 15 subjects at least once.',
    icon: 'compass',
    requirement: { type: 'subjects_mastered', value: 15 },
    unlocked: false,
    progress: 0,
  },
];

/**
 * ProgressionService - Manages character unlocks, achievements, and XP tracking
 */
export class ProgressionService {
  private data: ProgressionData;

  constructor() {
    this.data = this.load();
  }

  private createDefaultData(): ProgressionData {
    return {
      version: PROGRESSION_VERSION,
      characters: [...DEFAULT_CHARACTERS],
      achievements: [...DEFAULT_ACHIEVEMENTS],
      selectedCharacterId: 'knight',
      totalXP: 0,
      highestStreak: 0,
      perfectRuns: 0,
      subjectsMastered: [],
    };
  }

  private load(): ProgressionData {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.version === PROGRESSION_VERSION) {
          return parsed;
        }
      }
    } catch {
      console.warn('Failed to load progression data, creating new');
    }
    return this.createDefaultData();
  }

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch {
      console.warn('Failed to save progression data');
    }
  }

  // =========================================================================
  // XP Management
  // =========================================================================

  /**
   * Add XP and check for character unlocks
   */
  addXP(amount: number): void {
    this.data.totalXP += amount;
    this.checkCharacterUnlocks();
    this.checkAchievements('xp', this.data.totalXP);
    this.save();
  }

  getTotalXP(): number {
    return this.data.totalXP;
  }

  // =========================================================================
  // Character Management
  // =========================================================================

  /**
   * Check if any characters should be unlocked based on current XP
   */
  private checkCharacterUnlocks(): void {
    for (const character of this.data.characters) {
      if (!character.unlocked && this.data.totalXP >= character.xpRequired) {
        character.unlocked = true;
        character.unlockedAt = new Date().toISOString();

        // Emit unlock event for UI notification
        gameBridge.emit('character-unlocked' as any, {
          character: character,
          xpRequired: character.xpRequired,
        });

        console.info(`Character unlocked: ${character.name}`);
      }
    }
  }

  /**
   * Get all characters (locked and unlocked)
   */
  getAllCharacters(): UnlockableCharacter[] {
    return [...this.data.characters];
  }

  /**
   * Get only unlocked characters
   */
  getUnlockedCharacters(): UnlockableCharacter[] {
    return this.data.characters.filter(c => c.unlocked);
  }

  /**
   * Get next character to unlock
   */
  getNextUnlock(): UnlockableCharacter | null {
    const locked = this.data.characters
      .filter(c => !c.unlocked)
      .sort((a, b) => a.xpRequired - b.xpRequired);
    return locked[0] || null;
  }

  /**
   * Get progress toward next unlock (0-100)
   */
  getUnlockProgress(): number {
    const next = this.getNextUnlock();
    if (!next) return 100;

    const previousUnlock = this.data.characters
      .filter(c => c.unlocked)
      .sort((a, b) => b.xpRequired - a.xpRequired)[0];

    const baseXP = previousUnlock?.xpRequired || 0;
    const xpNeeded = next.xpRequired - baseXP;
    const currentProgress = this.data.totalXP - baseXP;

    return Math.min(100, Math.round((currentProgress / xpNeeded) * 100));
  }

  /**
   * Select a character (must be unlocked)
   */
  selectCharacter(characterId: string): boolean {
    const character = this.data.characters.find(c => c.id === characterId);
    if (character && character.unlocked) {
      this.data.selectedCharacterId = characterId;
      this.save();
      return true;
    }
    return false;
  }

  /**
   * Get currently selected character
   */
  getSelectedCharacter(): UnlockableCharacter {
    const selected = this.data.characters.find(c => c.id === this.data.selectedCharacterId);
    return selected || this.data.characters[0];
  }

  // =========================================================================
  // Achievement Management
  // =========================================================================

  /**
   * Check and unlock achievements based on type and value
   */
  checkAchievements(type: AchievementRequirement['type'], value: number): void {
    for (const achievement of this.data.achievements) {
      if (achievement.unlocked) continue;
      if (achievement.requirement.type !== type) continue;

      // Update progress for trackable achievements
      if (achievement.progress !== undefined) {
        achievement.progress = value;
      }

      // Check if requirement met
      if (value >= achievement.requirement.value) {
        achievement.unlocked = true;
        achievement.unlockedAt = new Date().toISOString();

        // Emit unlock event
        gameBridge.emit('achievement-unlocked' as any, {
          achievement: achievement,
        });

        console.info(`Achievement unlocked: ${achievement.name}`);
      }
    }
    this.save();
  }

  /**
   * Record a streak (for streak achievements)
   */
  recordStreak(streak: number): void {
    if (streak > this.data.highestStreak) {
      this.data.highestStreak = streak;
      this.checkAchievements('streak', streak);
    }
  }

  /**
   * Record a perfect run
   */
  recordPerfectRun(): void {
    this.data.perfectRuns++;
    this.checkAchievements('perfect_runs', this.data.perfectRuns);
  }

  /**
   * Record subject studied
   */
  recordSubjectStudied(subject: string): void {
    if (!this.data.subjectsMastered.includes(subject)) {
      this.data.subjectsMastered.push(subject);
      this.checkAchievements('subjects_mastered', this.data.subjectsMastered.length);
    }
  }

  /**
   * Record correct answer (for correct_answers achievements)
   */
  recordCorrectAnswer(): void {
    // Get current count from profile store and check achievements
    // This is called separately to decouple from ProfileStore
  }

  /**
   * Get all achievements
   */
  getAllAchievements(): Achievement[] {
    return [...this.data.achievements];
  }

  /**
   * Get unlocked achievements
   */
  getUnlockedAchievements(): Achievement[] {
    return this.data.achievements.filter(a => a.unlocked);
  }

  /**
   * Get achievement progress summary
   */
  getAchievementProgress(): { unlocked: number; total: number; percentage: number } {
    const unlocked = this.data.achievements.filter(a => a.unlocked).length;
    const total = this.data.achievements.length;
    return {
      unlocked,
      total,
      percentage: Math.round((unlocked / total) * 100),
    };
  }

  // =========================================================================
  // Stats
  // =========================================================================

  getHighestStreak(): number {
    return this.data.highestStreak;
  }

  getPerfectRuns(): number {
    return this.data.perfectRuns;
  }

  getSubjectsStudied(): string[] {
    return [...this.data.subjectsMastered];
  }

  /**
   * Reset all progression (for testing)
   */
  reset(): void {
    this.data = this.createDefaultData();
    this.save();
  }
}

// Singleton instance
export const progressionService = new ProgressionService();
